import { createApi } from '@reduxjs/toolkit/query/react';

import { IUploadAvatarPayload } from '../../types/interfaces/app-file';
import { IExercise } from '../../types/interfaces/exercise';
import { IProgramCollection } from '../../types/interfaces/program-collection';
import { IProgramItem } from '../../types/interfaces/program-item';
import { IProgramSet } from '../../types/interfaces/program-set';
import { HTTP_METHOD, PROGRAMS, REDUX_CACHE } from '../constants';
import * as endpoints from '../endpoints';
import {
  TransformedErrorResponseWithNotification,
  getBaseQuery,
  getListProvidesTags,
} from '../generic';

export const programsApi = createApi({
  reducerPath: 'programsApi',
  tagTypes: [
    PROGRAMS.AVAILABLE,
    PROGRAMS.PROGRAMS,
    PROGRAMS.PROGRAM_SETS,
    PROGRAMS.PROGRAM_ITEMS,
    PROGRAMS.EXERCISES,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(),
  // global configuration for the api
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getUserProgramCollectionsApi: builder.query<IProgramCollection[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Program.COLLECTION,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => getListProvidesTags(result, PROGRAMS.PROGRAMS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getAvailableUserProgramItemsApi: builder.query<IProgramItem[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Program.AVAILABLE,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) => getListProvidesTags(result, PROGRAMS.AVAILABLE),
      ...TransformedErrorResponseWithNotification(),
    }),
    getProgramCollectionByIdApi: builder.query<IProgramCollection, string>({
      keepUnusedDataFor: 3600,
      query(id) {
        return {
          url: endpoints.Program.COLLECTION_BY_ID(id),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => [{ type: PROGRAMS.PROGRAMS, id }],
      // ...TransformedErrorResponseWithNotification(),
    }),
    postPutProgramCollectionApi: builder.mutation<
      void,
      {
        id?: string;
        body: Partial<Omit<IProgramCollection, 'avatar'>> & {
          avatar?: IUploadAvatarPayload;
        };
      }
    >({
      query({ id, body }) {
        return {
          url: id
            ? endpoints.Program.COLLECTION_BY_ID(id)
            : endpoints.Program.COLLECTION,
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAMS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: PROGRAMS.PROGRAMS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteProgramsApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.Program.COLLECTION_BY_ID(id),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAMS,
              id: params.id,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getProgramItemsApi: builder.query<IProgramItem[], void>({
      keepUnusedDataFor: 3600,
      query(args) {
        return {
          url: endpoints.Program.ITEMS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, PROGRAMS.PROGRAM_ITEMS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getProgramItemByIdApi: builder.query<IProgramItem, string>({
      keepUnusedDataFor: 3600,
      query(id) {
        return {
          url: endpoints.Program.ITEMS_BY_ID(id),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => {
        return [{ type: PROGRAMS.PROGRAM_ITEMS, id }];
      },
      // ...TransformedErrorResponseWithNotification(),
    }),
    getProgramItemExercisesApi: builder.query<IExercise[], string>({
      keepUnusedDataFor: 3600,
      query(itemId) {
        return {
          url: endpoints.Program.EXERCISES(itemId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => {
        return [{ type: PROGRAMS.EXERCISES, id }];
      },
      ...TransformedErrorResponseWithNotification(),
    }),

    postPutProgramItemApi: builder.mutation<
      void,
      { id: string; body: IProgramItem }
    >({
      query({ id, body }) {
        return {
          url: id ? endpoints.Program.ITEMS_BY_ID(id) : endpoints.Program.ITEMS,
          method: id ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAM_ITEMS,
              id: params.id,
            },
            {
              type: PROGRAMS.PROGRAMS,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteProgramItemsApi: builder.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: endpoints.Program.ITEMS_BY_ID(id),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAM_ITEMS,
              id: params.id,
            },
            {
              type: PROGRAMS.PROGRAMS,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    copyProgramItemApi: builder.mutation<
      IProgramItem,
      { itemId: string; title: string; programCollectionId: string }
    >({
      query({ itemId, title, programCollectionId }) {
        return {
          url: endpoints.Program.COPY(itemId),
          method: HTTP_METHOD.POST,
          body: { title, programCollectionId },
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAMS,
            },
            {
              type: PROGRAMS.PROGRAM_ITEMS,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    shareProgramItemApi: builder.mutation<
      void,
      { itemId: string; userId: string }
    >({
      query({ itemId, userId }) {
        return {
          url: endpoints.Program.USER(itemId, userId),
          method: HTTP_METHOD.POST,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            { type: PROGRAMS.PROGRAM_ITEMS, id: params.itemId },
            {
              type: PROGRAMS.PROGRAMS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: PROGRAMS.AVAILABLE,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    unshareProgramItemApi: builder.mutation<
      void,
      { itemId: string; userId: string }
    >({
      query({ itemId, userId }) {
        return {
          url: endpoints.Program.USER(itemId, userId),
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            { type: PROGRAMS.PROGRAM_ITEMS, id: params.itemId },
            {
              type: PROGRAMS.PROGRAMS,
              id: REDUX_CACHE.LIST,
            },
            {
              type: PROGRAMS.AVAILABLE,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getProgramSetsApi: builder.query<IProgramSet[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.Program.SETS,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, PROGRAMS.PROGRAM_SETS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getProgramSetByIdApi: builder.query<IProgramSet, string>({
      keepUnusedDataFor: 3600,
      query(setId) {
        return {
          url: endpoints.Program.SETS_BY_ID(setId),
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result, error, id) => {
        return [{ type: PROGRAMS.PROGRAM_SETS, id }];
      },
      // ...TransformedErrorResponseWithNotification(),
    }),
    postPutProgramSetApi: builder.mutation<
      void,
      { setId?: string; body: IProgramSet }
    >({
      query({ setId, body }) {
        return {
          url: endpoints.Program.SETS + (setId ? `/${setId}` : ''),
          method: setId ? HTTP_METHOD.PUT : HTTP_METHOD.POST,
          body,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAM_SETS,
              id: params.setId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    deleteProgramSetApi: builder.mutation<void, { setId: string }>({
      query({ setId }) {
        return {
          url: `${endpoints.Program.SETS}/${setId}`,
          method: HTTP_METHOD.DELETE,
        };
      },
      invalidatesTags: (result, error, params) => {
        if (error) {
          return [REDUX_CACHE.ABORT_INVALIDATION];
        } else {
          return [
            {
              type: PROGRAMS.PROGRAM_SETS,
              id: params.setId,
            },
          ];
        }
      },
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const {
  useGetAvailableUserProgramItemsApiQuery: useGetAvailableUserProgramItems,
  useGetUserProgramCollectionsApiQuery: useGetUserProgramCollections,
  useGetProgramCollectionByIdApiQuery: useGetProgramCollectionById,
  useLazyGetProgramCollectionByIdApiQuery: useLazyGetProgramCollectionById,
  usePostPutProgramCollectionApiMutation: usePostPutProgramCollection,
  useDeleteProgramsApiMutation: useDeletePrograms,
  useGetProgramItemsApiQuery: useGetProgramItems,
  useGetProgramItemByIdApiQuery: useGetProgramItemById,
  useLazyGetProgramItemByIdApiQuery: useLazyGetProgramItemById,
  usePostPutProgramItemApiMutation: usePostPutProgramItem,
  useCopyProgramItemApiMutation: useCopyProgramItem,
  useDeleteProgramItemsApiMutation: useDeleteProgramItems,
  useShareProgramItemApiMutation: useShareProgramItem,
  useUnshareProgramItemApiMutation: useUnshareProgramItem,
  useLazyGetProgramItemExercisesApiQuery: useLazyGetProgramItemExercises,
  useGetProgramSetsApiQuery: useGetProgramSets,
  useGetProgramSetByIdApiQuery: useGetProgramSetById,
  useLazyGetProgramSetByIdApiQuery: useLazyGetProgramSetById,
  usePostPutProgramSetApiMutation: usePostPutProgramSet,
  useDeleteProgramSetApiMutation: useDeleteProgramSet,
} = programsApi;
